import { searchHealthResources } from '@/app/(main)/_components/search-modal/_services/search-health-resources.service';
import { searchHealthResourcesByType } from '@/app/(main)/_components/search-modal/_services/search-health-resources-by-type.service';
import { findProceduresByTerm } from '@/app/(main)/_components/search-modal/_services/search-api/find-procedures-by-term.service';
import { findProfessionalsByTerm } from '@/app/(main)/_components/search-modal/_services/search-api/find-professionals-by-term.service';
import { findPartnerFacilitiesByTerm } from '@/app/(main)/_components/search-modal/_services/search-api/find-partner-facilities-by-term.service';
import {
  mapFacilitiesToSearchResults,
  mapProcedureToSearchResult,
  mapProfessionalToSearchResult,
} from '@/app/(main)/_components/search-modal/_services/search-api/map-to-search-result.util';
import { RESULTS_PAGE_SIZE, SECTION_RESULT_LIMIT } from '@/app/(main)/_components/search-modal/_consts/search-results.consts';
import {
  AllSearchResults,
  SearchCategoryResults,
  SearchHealthResourcesResponse,
  SearchResultsSource,
  SearchType,
} from '@/app/(main)/_components/search-modal/search-modal.types';

type CategorizedResults = Omit<AllSearchResults, SearchType.ALL>;

export interface SearchSourceParams {
  source?: SearchResultsSource;
  citySlug?: string;
}

interface FetchResultsByTypeParams extends SearchSourceParams {
  term: string;
  searchType: SearchType;
  page?: number;
  limit?: number;
}

interface SearchApiPageParams {
  term: string;
  citySlug?: string;
  page: number;
  limit: number;
}

interface SearchApiProcedurePageParams extends SearchApiPageParams {
  categories?: string[];
}

interface FetchProceduresByCategoryParams extends SearchSourceParams {
  term: string;
  category: SearchType;
  page?: number;
  limit?: number;
}

function toCategoryResults(
  response: SearchHealthResourcesResponse,
  alreadyLoaded = 0,
): SearchCategoryResults {
  return {
    results: response.results,
    hasMore: alreadyLoaded + response.results.length < response.total,
  };
}

function toEveryCategoryBucket(categories: CategorizedResults): SearchCategoryResults {
  return {
    results: [
      ...categories[SearchType.PROCEDURE].results,
      ...categories[SearchType.PROFESSIONAL].results,
      ...categories[SearchType.PARTNER].results,
    ],
    hasMore: Object.values(categories).some((category) => category.hasMore),
  };
}

async function fetchProceduresFromSearchApi(params: SearchApiProcedurePageParams): Promise<SearchCategoryResults> {
  const { procedures, hasMore } = await findProceduresByTerm(params);

  return { results: procedures.map(mapProcedureToSearchResult), hasMore };
}

async function fetchProfessionalsFromSearchApi(params: SearchApiPageParams): Promise<SearchCategoryResults> {
  const { professionals, hasMore } = await findProfessionalsByTerm(params);

  return { results: professionals.map(mapProfessionalToSearchResult), hasMore };
}

async function fetchPartnersFromSearchApi(params: SearchApiPageParams): Promise<SearchCategoryResults> {
  const { facilities, hasMore } = await findPartnerFacilitiesByTerm(params);

  return { results: mapFacilitiesToSearchResults(facilities), hasMore };
}

async function fetchCategoriesFromSearchApi(params: SearchApiPageParams): Promise<CategorizedResults> {
  const [procedures, professionals, partners] = await Promise.all([
    fetchProceduresFromSearchApi(params),
    fetchProfessionalsFromSearchApi(params),
    fetchPartnersFromSearchApi(params),
  ]);

  return {
    [SearchType.PROCEDURE]: procedures,
    [SearchType.PROFESSIONAL]: professionals,
    [SearchType.PARTNER]: partners,
  };
}

async function fetchCategoriesFromLegacy(term: string): Promise<CategorizedResults> {
  const [procedures, professionals, partners] = await Promise.all([
    searchHealthResourcesByType(term, SearchType.PROCEDURE, 1, SECTION_RESULT_LIMIT),
    searchHealthResourcesByType(term, SearchType.PROFESSIONAL, 1, SECTION_RESULT_LIMIT),
    searchHealthResourcesByType(term, SearchType.PARTNER, 1, SECTION_RESULT_LIMIT),
  ]);

  return {
    [SearchType.PROCEDURE]: toCategoryResults(procedures),
    [SearchType.PROFESSIONAL]: toCategoryResults(professionals),
    [SearchType.PARTNER]: toCategoryResults(partners),
  };
}

async function fetchPageFromSearchApi(
  searchType: SearchType,
  params: SearchApiPageParams,
): Promise<SearchCategoryResults> {
  switch (searchType) {
    case SearchType.PROCEDURE:
      return fetchProceduresFromSearchApi(params);
    case SearchType.PROFESSIONAL:
      return fetchProfessionalsFromSearchApi(params);
    case SearchType.PARTNER:
      return fetchPartnersFromSearchApi(params);
    default:
      return toEveryCategoryBucket(await fetchCategoriesFromSearchApi(params));
  }
}

export async function fetchMixedResults(term: string): Promise<AllSearchResults> {
  const [all, procedures, professionals, partners] = await Promise.all([
    searchHealthResources(term),
    searchHealthResourcesByType(term, SearchType.PROCEDURE),
    searchHealthResourcesByType(term, SearchType.PROFESSIONAL),
    searchHealthResourcesByType(term, SearchType.PARTNER),
  ]);

  return {
    [SearchType.ALL]: toCategoryResults(all),
    [SearchType.PROCEDURE]: toCategoryResults(procedures),
    [SearchType.PROFESSIONAL]: toCategoryResults(professionals),
    [SearchType.PARTNER]: toCategoryResults(partners),
  };
}

export async function fetchCategorizedResults(
  term: string,
  { source = 'legacy', citySlug }: SearchSourceParams = {},
): Promise<AllSearchResults> {
  const categories = source === 'search-api'
    ? await fetchCategoriesFromSearchApi({ term, citySlug, page: 1, limit: SECTION_RESULT_LIMIT })
    : await fetchCategoriesFromLegacy(term);

  return {
    ...categories,
    [SearchType.ALL]: toEveryCategoryBucket(categories),
  };
}

export async function fetchResultsByType({
  term,
  searchType,
  page = 1,
  limit = RESULTS_PAGE_SIZE,
  source = 'legacy',
  citySlug,
}: FetchResultsByTypeParams): Promise<SearchCategoryResults> {
  if (source === 'search-api') {
    return fetchPageFromSearchApi(searchType, { term, citySlug, page, limit });
  }

  const response = searchType === SearchType.ALL
    ? await searchHealthResources(term, page, limit)
    : await searchHealthResourcesByType(term, searchType, page, limit);

  return toCategoryResults(response, (page - 1) * limit);
}

export async function fetchProceduresByCategory({
  term,
  category,
  page = 1,
  limit = RESULTS_PAGE_SIZE,
  source = 'legacy',
  citySlug,
}: FetchProceduresByCategoryParams): Promise<SearchCategoryResults> {
  if (source === 'search-api') {
    return fetchProceduresFromSearchApi({ term, citySlug, categories: [category], page, limit });
  }

  const response = await searchHealthResourcesByType(term, category, page, limit);

  return toCategoryResults(response, (page - 1) * limit);
}
